/**
 * SectionGhostPlaceholder — rendered inside SectionCard when the section has
 * no data.  Always renders a section-specific prompt + "Draft this section →"
 * CTA.  Optionally renders a quick-apply chip if a draftValue is provided.
 */

import { useState } from 'react';

export type SectionKey =
  | 'objective'
  | 'audience'
  | 'insight'
  | 'brand_voice'
  | 'message'
  | 'creative_context'
  | 'mandatories'
  | 'success_metrics';

export const SECTION_PROMPTS: Record<SectionKey, string> = {
  objective: 'What is the campaign goal, type, and funnel stage?',
  audience: 'Who is the target persona for this campaign?',
  insight: 'What tension or unmet need drives this persona?',
  brand_voice: 'How should the brand sound — tone, style, personality?',
  message: 'What is the core hook, headline direction, and CTA?',
  creative_context: 'What visual style and real-world scenarios fit this persona?',
  mandatories: 'What claims, disclaimers, or brand rules must appear?',
  success_metrics: 'What KPIs and creative signals define success?',
};

const INSET_STYLE: Record<SectionKey, string> = {
  objective: 'border-sky-200 bg-sky-50/40',
  audience: 'border-violet-200 bg-violet-50/40',
  insight: 'border-amber-200 bg-amber-50/40',
  brand_voice: 'border-teal-200 bg-teal-50/40',
  message: 'border-rose-200 bg-rose-50/40',
  creative_context: 'border-indigo-200 bg-indigo-50/40',
  mandatories: 'border-orange-200 bg-orange-50/40',
  success_metrics: 'border-emerald-200 bg-emerald-50/40',
};

interface SectionGhostPlaceholderProps {
  sectionKey: SectionKey;
  draftValue?: string;
  onDraftSection: () => void;
  onQuickApply?: () => Promise<void>;
  canInteract: boolean;
}

export function SectionGhostPlaceholder({
  sectionKey,
  draftValue,
  onDraftSection,
  onQuickApply,
  canInteract,
}: SectionGhostPlaceholderProps) {
  const [applyState, setApplyState] = useState<'idle' | 'loading' | 'error'>('idle');
  const prompt = SECTION_PROMPTS[sectionKey];
  const insetStyle = INSET_STYLE[sectionKey];
  const truncatedDraft = draftValue ? truncate(draftValue, 60) : null;

  const handleApply = async () => {
    if (!onQuickApply || applyState === 'loading') return;
    setApplyState('loading');
    try {
      await onQuickApply();
    } catch {
      setApplyState('error');
      setTimeout(() => setApplyState('idle'), 3000);
    }
  };

  return (
    <div
      className={`mt-1 rounded-lg border border-dashed ${insetStyle} px-4 py-3 flex flex-col gap-2.5`}
    >
      {draftValue && canInteract && onQuickApply && (
        <div className="flex items-start gap-2">
          <button
            onClick={handleApply}
            disabled={applyState === 'loading'}
            title={draftValue}
            className={`group flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition text-left max-w-full ${
              applyState === 'error'
                ? 'bg-rose-50 border-rose-200 text-rose-600'
                : 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100'
            } ${applyState === 'loading' ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
          >
            {applyState === 'loading' ? (
              <span className="w-3 h-3 border border-violet-400 border-t-transparent rounded-full animate-spin shrink-0" />
            ) : applyState === 'error' ? (
              <span className="shrink-0">✕</span>
            ) : (
              <span className="shrink-0 text-violet-400">+</span>
            )}
            <span className="truncate">
              {applyState === 'error' ? 'Failed — try again' : truncatedDraft}
            </span>
          </button>
        </div>
      )}

      <p className="text-[12px] text-slate-400 leading-snug">{prompt}</p>

      {canInteract && (
        <button
          onClick={onDraftSection}
          className="self-start text-[11px] font-medium rounded border border-slate-200 px-2.5 py-1 text-slate-500 bg-white hover:border-brand-purple hover:text-brand-purple transition"
        >
          Draft this section →
        </button>
      )}
    </div>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max).trimEnd()}…` : s;
}
